import React from 'react';
import { JobApplication, ApplicationStatus } from '../../types/application';
import InterviewBoard from './InterviewBoard';
import { motion } from 'framer-motion';
import { FiBriefcase, FiCalendar } from 'react-icons/fi';
import { Interview } from '../../types/interview';
import { InterviewStage } from '../../types/interview';

interface InterviewPipelineProps {
  applications: JobApplication[];
  interview: Interview | null;
  onStatusChange?: (applicationId: number, newStatus: ApplicationStatus) => void;
  onOpenNotes: (interview: Interview, stage: InterviewStage) => void;
}

const InterviewPipeline: React.FC<InterviewPipelineProps> = ({
  applications,
  interview,
  onStatusChange,
  onOpenNotes
}) => {
  const interviewingApplications = applications.filter(app => 
    app.status === ApplicationStatus.INTERVIEWING
  );

  if (interviewingApplications.length === 0) {
    return null;
  }

  return (
    <motion.div 
      className="mt-12 bg-white rounded-xl shadow-sm p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Interview Pipeline</h2>
      <div className="space-y-8">
        {interviewingApplications.map(application => (
          <motion.div 
            key={application.id} 
            className="bg-gray-50 rounded-lg p-6"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {application.company}
                </h3>
                <div className="flex items-center gap-4 mt-2 text-gray-600">
                  <div className="flex items-center">
                    <FiBriefcase className="w-4 h-4 mr-2" />
                    <span className="text-sm">{application.title}</span>
                  </div>
                  <div className="flex items-center">
                    <FiCalendar className="w-4 h-4 mr-2" />
                    <span className="text-sm">
                      Added {new Date(application.updatedAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 text-sm font-medium text-purple-700 bg-purple-100 rounded-full">
                  Interviewing
                </span>
              </div>
            </div>
            <InterviewBoard 
              applicationId={application.id}
              jobId={application.job_id}
              onStatusChange={onStatusChange}
              onOpenNotes={onOpenNotes}
            />
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default InterviewPipeline; 